const MEDPLUM_LOGOUT_CHANNEL = 'eluve:medplum:logout';
const MEDPLUM_LOGOUT_MESSAGE = 'logout';
const TAB_ID =
  globalThis.crypto?.randomUUID?.() ?? `tab-${Math.random().toString(36)}`;

interface MedplumLogoutMessage {
  type: typeof MEDPLUM_LOGOUT_MESSAGE;
  from: string;
}

const openLogoutChannel = (): BroadcastChannel | undefined => {
  if (typeof BroadcastChannel === 'undefined') return undefined;
  try {
    return new BroadcastChannel(MEDPLUM_LOGOUT_CHANNEL);
  } catch {
    // Opaque-origin and storage-partitioned contexts may reject construction.
    // Tab-local cleanup must still proceed when cross-tab signaling is absent.
    return undefined;
  }
};

export const broadcastMedplumLogout = (): void => {
  const channel = openLogoutChannel();
  if (!channel) return;
  // postMessage determines its destinations before returning, so closing the
  // short-lived sender immediately cannot cancel the queued deliveries.
  try {
    channel.postMessage({
      type: MEDPLUM_LOGOUT_MESSAGE,
      from: TAB_ID,
    } satisfies MedplumLogoutMessage);
  } catch {
    // A signaling failure must not prevent the initiating tab from clearing.
  } finally {
    channel.close();
  }
};

export const subscribeToMedplumLogout = (
  onLogout: () => void | Promise<void>,
): (() => void) => {
  const channel = openLogoutChannel();
  if (!channel) return () => undefined;

  const handleMessage = (event: MessageEvent<unknown>) => {
    const data =
      typeof event.data === 'object' && event.data !== null
        ? (event.data as Partial<MedplumLogoutMessage>)
        : undefined;
    if (
      data?.type !== MEDPLUM_LOGOUT_MESSAGE ||
      typeof data.from !== 'string' ||
      data.from === TAB_ID
    ) {
      return;
    }
    Promise.resolve(onLogout()).catch(() => undefined);
  };
  channel.addEventListener('message', handleMessage);

  return () => {
    channel.removeEventListener('message', handleMessage);
    channel.close();
  };
};
